// Only fixed application metadata enters breadcrumbs. Never serialize a block,
// DOM node, input value, program, URL, or arbitrary event properties.
const enumFields = {
  screen: ['game', 'title', 'language', 'explorers', 'credits', 'story', 'worlds', 'map'],
  renderer: ['pending', 'webgl', 'canvas'],
  execution_mode: ['run', 'step'],
  explorer: ['marco', 'sophia'],
};
const numberFields = ['case_index', 'content_revision', 'moves', 'tile_index', 'block_count', 'texture_count', 'canvas_width', 'canvas_height'];
const booleanFields = ['loading', 'busy', 'running', 'booting', 'dragging', 'reduced_motion', 'recovering'];
const controls = new Set(['run', 'step', 'reset', 'undo', 'clear', 'next', 'hint', 'backpack',
  'language', 'block-language', 'title-play', 'marco', 'sophia', 'tab-actions', 'tab-loops', 'tab-conditions',
  'guide-close', 'close-feedback', 'map', 'worlds']);
const controlSelector = [...controls].map(id => `#${id}`).join(',');

export function diagnosticFields(source = {}) {
  const result = {};
  for (const [key, values] of Object.entries(enumFields)) if (values.includes(source[key])) result[key] = source[key];
  for (const key of numberFields) if (Number.isFinite(source[key])) result[key] = Math.max(-1, Math.min(1e7, Math.round(source[key])));
  for (const key of booleanFields) if (typeof source[key] === 'boolean') result[key] = source[key];
  if (/^(jungle|temple|aegean|winter)-\d+$/.test(source.level_id)) result.level_id = source.level_id;
  if (controls.has(source.control) || source.control === 'blockly') result.control = source.control;
  return result;
}

export function createErrorReporting({ sdk, document, enabled = true }) {
  let state = () => ({}), reporting = false;
  const reported = new WeakSet();
  const api = {
    setState(provider) { state = provider; },
    snapshot() {
      try { return diagnosticFields(state()); }
      catch { return { diagnostics_unavailable: true }; }
    },
    step(name, properties = {}) {
      if (!enabled || reporting || !/^[a-z][a-z_]{0,63}$/.test(name)) return;
      try { sdk.addExceptionStep(name, { ...api.snapshot(), ...diagnosticFields(properties) }); }
      catch { /* Diagnostics must never interrupt play. */ }
    },
    report(error, phase) {
      if (!enabled || reporting) return;
      const object = error !== null && (typeof error === 'object' || typeof error === 'function');
      if (object && reported.has(error)) return;
      reporting = true;
      try {
        // Keep the original Error and stack; no synthetic wrapping of real errors.
        sdk.captureException(error, { error_phase: /^[a-z_]{1,40}$/.test(phase) ? phase : 'unknown',
          game_state: api.snapshot(),
          ...(/^\/(assets|worlds)\/[a-zA-Z0-9_./-]+$/.test(error?.resource) ? { failed_resource: error.resource } : {}),
          ...(Number.isInteger(error?.status) ? { resource_status: error.status } : {}),
          ...(Number.isInteger(error?.attempts) ? { resource_attempts: error.attempts } : {}),
        });
        if (object) reported.add(error);
      } catch { /* An SDK failure must not recursively report itself. */ }
      finally { reporting = false; }
    },
    listen() {
      if (!enabled) return () => {};
      const record = event => {
        try {
          const target = event.target;
          // No labels, text, field values, coordinates or generated Blockly IDs.
          const control = target?.closest?.(controlSelector)?.id;
          const name = controls.has(control) ? control
            : target?.closest?.('.blocklyDraggable, .blocklyToolbox, .command-shortcut') ? 'blockly' : null;
          if (!name) return;
          if (event.type === 'keydown' && !['Enter', ' ', 'Escape'].includes(event.key)) return;
          api.step(event.type === 'keydown' ? 'keyboard_action' : event.type === 'click' ? 'control_clicked' : 'pointer_started', { control: name });
        } catch { /* DOM changes must not break event dispatch. */ }
      };
      // Capture phase runs before handlers which might throw. No preventDefault.
      for (const type of ['pointerdown', 'click', 'keydown']) document.addEventListener(type, record, true);
      return () => { for (const type of ['pointerdown', 'click', 'keydown']) document.removeEventListener(type, record, true); };
    },
  };
  return api;
}
